/**
 * IdleNudge — proactive "Still editing?" prompt to keep the lock alive.
 *
 * Watches for keystrokes / pointer activity inside the document. After
 * `idleThresholdMs` of no activity (default 8 min), shows a modal AND plays
 * an audible ding-ding so an away-from-keyboard user can be pulled back.
 *
 * Why 8 min for a 10-min TTL: gives the user 2 minutes to react before the
 * lock expires. If they click "Keep editing", we fire an immediate heartbeat
 * (via the `ping` callback) and reset the idle timer. If they don't react,
 * the lock will expire in ~2 min and the LostLockModal takes over.
 *
 * The audio uses the Web Audio API directly so we don't need to ship an
 * asset. Two short 800Hz pulses at low volume — recognizably a "ding ding".
 * Some browsers block audio without prior user interaction; if blocked, the
 * modal still shows and the visual nudge does its job alone.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { buttonClass } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { IconClock } from './ui/icons';

interface Props {
  /** Are we currently the lock holder? Disable the nudge otherwise. */
  active: boolean;
  /** Trigger an immediate heartbeat. Returns true if the lock is still held. */
  ping: () => Promise<boolean>;
  /** Optional override of the idle threshold in ms (default: 8 minutes). */
  idleThresholdMs?: number;
  /** Optional override of the dismiss-after-prompt timeout (default: 90s). */
  responseWindowMs?: number;
}

const DEFAULT_IDLE_THRESHOLD_MS = 8 * 60 * 1000;
const DEFAULT_RESPONSE_WINDOW_MS = 90 * 1000;

function playDingDing() {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const playPulse = (startSec: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime + startSec);
      // Quick attack + decay envelope for a clean "ding"
      gain.gain.setValueAtTime(0, ctx.currentTime + startSec);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + startSec + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startSec + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + startSec);
      osc.stop(ctx.currentTime + startSec + 0.4);
    };
    playPulse(0);
    playPulse(0.45);
    // Close the context after the audio finishes so we don't hold one open
    // for every nudge over a long-lived tab.
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1200);
  } catch {
    // Audio blocked or unsupported — modal is the primary signal anyway.
  }
}

const IdleNudge: React.FC<Props> = ({
  active,
  ping,
  idleThresholdMs = DEFAULT_IDLE_THRESHOLD_MS,
  responseWindowMs = DEFAULT_RESPONSE_WINDOW_MS,
}) => {
  const [showing, setShowing] = useState(false);
  const lastActivityRef = useRef<number>(Date.now());
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const responseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleIdleCheck = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = Math.max(0, idleThresholdMs - elapsed);
    idleTimerRef.current = setTimeout(() => {
      setShowing(true);
      playDingDing();
      // If the user doesn't respond within the response window, stop showing
      // the modal — the lost-lock modal will take over once heartbeat fails.
      responseTimerRef.current = setTimeout(() => {
        setShowing(false);
      }, responseWindowMs);
    }, remaining);
  }, [idleThresholdMs, responseWindowMs]);

  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showing) return;       // don't reset while modal is up — let user respond
    scheduleIdleCheck();
  }, [scheduleIdleCheck, showing]);

  useEffect(() => {
    if (!active) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (responseTimerRef.current) clearTimeout(responseTimerRef.current);
      setShowing(false);
      return;
    }
    const events: (keyof DocumentEventMap)[] = ['keydown', 'mousedown', 'touchstart', 'pointermove'];
    events.forEach((e) => document.addEventListener(e, recordActivity, { passive: true }));
    scheduleIdleCheck();
    return () => {
      events.forEach((e) => document.removeEventListener(e, recordActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (responseTimerRef.current) clearTimeout(responseTimerRef.current);
    };
  }, [active, recordActivity, scheduleIdleCheck]);

  const handleKeepEditing = async () => {
    setShowing(false);
    if (responseTimerRef.current) clearTimeout(responseTimerRef.current);
    lastActivityRef.current = Date.now();
    await ping();              // immediate heartbeat — bumps expires_at server-side
    scheduleIdleCheck();
  };

  return (
    <Dialog
      open={showing}
      onClose={() => {}}
      dismissible={false}
      hideClose
      alert
      size="md"
      title={
        <span className="flex items-center gap-3">
          <IconClock className="h-6 w-6 shrink-0 text-primary" />
          Still editing?
        </span>
      }
      footer={
        <button type="button" onClick={handleKeepEditing} className={buttonClass('filled', 'md')}>
          Keep editing
        </button>
      }
    >
      <p className="m-0 text-sm text-on-surface">
        You've been idle for a while. Click below to keep your edit lock, otherwise it will expire in about 2 minutes and someone else will be able to edit this event.
      </p>
    </Dialog>
  );
};

export default IdleNudge;
